// Admin Children Management Page
import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import GlassCard from '../../components/admin/GlassCard';
import DataTable from '../../components/admin/DataTable';
import LoadingSpinner from '../../components/admin/LoadingSpinner';

const AdminChildren: React.FC = () => {
  const [children, setChildren] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingChild, setEditingChild] = useState<any>(null);
  const imageFileRef = React.useRef<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    medical_condition: '',
    story: '',
    image_url: '',
    status: 'active'
  });

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/children', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Failed to fetch children (${response.status})`);
      }
      const data = await response.json();
      setChildren(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching children:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (uploading) return;
    setUploading(true);
    
    try {
      let imageUrl = formData.image_url;
      const selectedImageFile = imageFileRef.current;
      
      // Upload image if a new file was selected
      if (selectedImageFile) {
        console.log('=== Starting upload process ===');
        console.log('Uploading image:', selectedImageFile.name, 'Type:', selectedImageFile.type, 'Size:', selectedImageFile.size);
        console.log('Target URL: http://localhost:5000/api/upload');
        
        const uploadImage = async () => {
          const uploadFormData = new FormData();
          uploadFormData.append('image', selectedImageFile);
          uploadFormData.append('category', 'children');

          console.log('FormData created, contents:', {
            hasImage: uploadFormData.has('image'),
            hasCategory: uploadFormData.has('category')
          });

          console.log('Making fetch request...');
          const uploadResponse = await fetch('http://localhost:5000/api/upload', {
            method: 'POST',
            body: uploadFormData
          });

          console.log('Upload response status:', uploadResponse.status, uploadResponse.statusText);

          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.json().catch(() => ({ error: 'Unknown server error' }));
            console.error('Upload failed:', errorData);
            throw new Error(errorData.error || 'Image upload failed');
          }

          return uploadResponse.json();
        };

        try {
          const uploadData = await uploadImage();
          console.log('Upload successful:', uploadData);
          imageUrl = uploadData.url;
        } catch (uploadError) {
          // Retry once for transient network errors (TypeError: Failed to fetch)
          const errorMessage = uploadError instanceof Error ? uploadError.message : String(uploadError);
          if (/Failed to fetch|NetworkError|network/i.test(errorMessage)) {
            console.warn('Upload failed due to network issue. Retrying once...');
            const uploadData = await uploadImage();
            console.log('Upload successful on retry:', uploadData);
            imageUrl = uploadData.url;
          } else {
            console.error('=== Upload error details ===');
            console.error('Error type:', uploadError instanceof Error ? uploadError.constructor.name : typeof uploadError);
            console.error('Error message:', errorMessage);
            console.error('Full error:', uploadError);
            throw new Error(`Upload failed: ${errorMessage}`);
          }
        }
      }
      
      const url = editingChild
        ? `http://localhost:5000/api/children/${editingChild.id}`
        : 'http://localhost:5000/api/children';
      
      // Calculate age from date of birth
      const calculateAge = (dob: string) => {
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age;
      };

      // Map frontend fields to backend expected fields
      const childData = {
        name: formData.name,
        age: calculateAge(formData.date_of_birth),
        date_of_birth: formData.date_of_birth,
        medical_condition: formData.medical_condition,
        bio: formData.story,
        image: imageUrl,
        order_position: 0,
        active: formData.status === 'active'
      };
      
      const saveChild = async () => fetch(url, {
        method: editingChild ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(childData)
      });

      let response: Response;
      try {
        response = await saveChild();
      } catch (networkError) {
        console.warn('Child save request failed due to network issue. Retrying once...', networkError);
        response = await saveChild();
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to save child profile' }));
        throw new Error(errorData.details ? `${errorData.error}: ${errorData.details}` : (errorData.error || 'Failed to save child profile'));
      }

      const savedChild = await response.json();

      setChildren((prev) => {
        if (editingChild) {
          return prev.map((child) => (child.id === savedChild.id ? savedChild : child));
        }
        return [savedChild, ...prev];
      });

      await fetchChildren();
      setShowAddForm(false);
      setEditingChild(null);
      imageFileRef.current = null;
      setImagePreview('');
      setFormData({ name: '', date_of_birth: '', medical_condition: '', story: '', image_url: '', status: 'active' });

      alert(`Child profile saved successfully (ID: ${savedChild.id})`);
    } catch (error) {
      console.error('Error saving child:', error);
      alert(`Error saving child profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (child: any) => {
    setEditingChild(child);
    
    // Calculate date of birth from age (approximate - will be today's date minus age years)
    const calculateDOBFromAge = (age: number) => {
      const today = new Date();
      const dob = new Date(today.getFullYear() - age, today.getMonth(), today.getDate());
      return dob.toISOString().split('T')[0];
    };
    
    setFormData({
      name: child.name,
      date_of_birth: calculateDOBFromAge(child.age),
      medical_condition: child.medical_condition || '',
      story: child.bio || '',
      image_url: child.image || '',
      status: child.active ? 'active' : 'inactive'
    });
    setImagePreview(child.image ? `http://localhost:5000${child.image}` : '');
    imageFileRef.current = null;
    setShowAddForm(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      imageFileRef.current = file;
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      imageFileRef.current = null;
      setImagePreview('');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this child profile?')) {
      try {
        await fetch(`http://localhost:5000/api/children/${id}`, {
          method: 'DELETE'
        });
        fetchChildren();
      } catch (error) {
        console.error('Error deleting child:', error);
      }
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'age', label: 'Age', sortable: true },
    { key: 'medical_condition', label: 'Medical Condition', sortable: true },
    {
      key: 'bio',
      label: 'Story',
      render: (value: string) => value ? value.substring(0, 40) + '...' : ''
    },
    {
      key: 'active',
      label: 'Status',
      render: (value: boolean) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            background: value ? '#D1FAE5' : '#FEE2E2',
            color: value ? '#065F46' : '#991B1B'
          }}
        >
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ color: '#111827', fontSize: '26px', fontWeight: '700', marginBottom: '6px', letterSpacing: '-0.02em' }}>
            Children Management
          </h1>
          <p style={{ color: '#6B7280', fontSize: '14px', fontWeight: '500' }}>
            Manage children profiles and their stories
          </p>
        </div>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingChild(null);
            imageFileRef.current = null;
            setImagePreview('');
            setFormData({ name: '', date_of_birth: '', medical_condition: '', story: '', image_url: '', status: 'active' });
          }}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: '#111827',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#1F2937';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#111827';
          }}
        >
          {showAddForm ? '✕ Cancel' : '+ Add Child'}
        </button>
      </div>

      {showAddForm && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <h3 style={{ color: '#111827', fontSize: '16px', fontWeight: '600', marginBottom: '16px', letterSpacing: '-0.01em' }}>
            {editingChild ? 'Edit Child Profile' : 'Add New Child'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  required
                  max={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                Medical Condition
              </label>
              <input
                type="text"
                value={formData.medical_condition}
                onChange={(e) => setFormData({ ...formData, medical_condition: e.target.value })}
                placeholder="e.g., Down Syndrome, Cerebral Palsy, Autism Spectrum"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#111827',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                Profile Image (370 x 451px) - JPG, PNG, HEIC supported
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#111827',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              {imagePreview && (
                <div style={{ marginTop: '12px' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: '185px',
                      height: '225.5px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid #E5E7EB'
                    }}
                  />
                </div>
              )}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                Story
              </label>
              <textarea
                value={formData.story}
                onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                required
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#111827',
                  outline: 'none',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#111827',
                  outline: 'none'
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={uploading}
              style={{
                padding: '10px 24px',
                borderRadius: '8px',
                border: 'none',
                background: uploading ? '#9CA3AF' : '#111827',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.7 : 1,
                transition: 'all 0.15s ease'
              }}
            >
              {uploading ? 'Uploading...' : (editingChild ? 'Update Child' : 'Add Child')}
            </button>
          </form>
        </GlassCard>
      )}

      <DataTable
        columns={columns}
        data={children}
        actions={(row) => (
          <>
            <button
              onClick={() => handleEdit(row)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #DBEAFE',
                background: '#EFF6FF',
                color: '#2563EB',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(row.id)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #FEE2E2',
                background: '#FEF2F2',
                color: '#DC2626',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              Delete
            </button>
          </>
        )}
      />
    </AdminLayout>
  );
};

export default AdminChildren;
